import { type MaybeRefOrGetter, toValue } from '@vueuse/core';
import { computed, type ComputedRef, unref } from 'vue';

export enum ModalCancelBehavior {
    NoReturn = 0,
    Resolve = 1,
    Reject = 2,
}

export type ModalSubmitCallback<S extends unknown[] = [], R = void> = (...args: S) => (Promise<R> | R);
export type ModalDialogOptions<S extends unknown[] = [], R = void> = {
    onCancel?: ModalCancelBehavior;
    onBeforeSubmit?: ModalSubmitCallback<S, R>;
};

export type ModalSubmitAction<T extends unknown[] = [], S extends unknown[] = [], R = void> =
    (options?: ModalDialogOptions<S, R> | null | undefined, ...args: T) => Promise<R>;

export type ModalDialogApi<T extends unknown[] = [], S extends unknown[] = [], R = void> = {
    showModal: ModalSubmitAction<T, S, R>;
    readonly isSubmitted: ComputedRef<boolean>;
};

export type ModalSubmitListener = () => void;

export type DefaultModalDialogApi<R = void> = ModalDialogApi<[], [], R> & {
    toggleModal(show?: boolean): Promise<void>;
    requestSubmit: ModalSubmitListener;
};

export function useModalDialogApi<T extends unknown[] = [], S extends unknown[] = [], R = void>({
    api,
    onBeforeShow,
    onPrepareSubmit,
    onBeforeSubmit,
    onAfterSubmit,
}: {
    api: MaybeRefOrGetter<DefaultModalDialogApi<R> | undefined>;
    onBeforeShow?: ModalSubmitCallback<T, void>;
    onPrepareSubmit?: () => S;
    onBeforeSubmit?: ModalSubmitCallback<S, R>;
    onAfterSubmit?: (submitResult: R) => R;
}): ModalDialogApi<T, S, R> {
    return {
        isSubmitted: computed(() => {
            const _api = toValue(api);
            return unref(_api?.isSubmitted) ?? false;
        }),
        showModal: (options = {}, ...args) => {
            const _api = toValue(api);
            if(!_api) {
                return Promise.reject(new Error('no api'));
            }
            return Promise.resolve()
                .then(() => onBeforeShow?.(...args))
                .then(() => _api.showModal({
                    ...options,
                    onBeforeSubmit: () => {
                        const submitArgs = onPrepareSubmit?.();
                        const _onBeforeSubmit = onBeforeSubmit ?? options?.onBeforeSubmit;
                        if(!_onBeforeSubmit) {
                            return Promise.resolve(undefined as R);
                        }
                        return _onBeforeSubmit(...((submitArgs ?? []) as S));
                    },
                }))
                .then((submitResult) => {
                    if(onAfterSubmit && unref(_api.isSubmitted)) {
                        return onAfterSubmit(submitResult);
                    }
                    return submitResult;
                });
        },
    };
}

export function wrapModalDialogRef<T extends unknown[] = [], S extends unknown[] = [], R = void>(
    api: MaybeRefOrGetter<ModalDialogApi<T, S, R> | undefined>,
): ModalDialogApi<T, S, R> {
    return {
        isSubmitted: computed(() => {
            const _api = toValue(api);
            return unref(_api?.isSubmitted) ?? false;
        }),
        showModal: (...args) => {
            const _api = toValue(api);
            if(!_api) {
                return Promise.reject(new Error('no api'));
            }
            return _api.showModal(...args);
        },
    };
}
